import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAudioRecorderOptions {
  maxDurationSeconds?: number;
  onRecordingComplete?: (file: File, durationSeconds: number) => void;
}

export const useAudioRecorder = (options: UseAudioRecorderOptions = {}) => {
  const { maxDurationSeconds, onRecordingComplete } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const stopRecordingStream = useCallback(() => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }

    recorder.stop();
  }, []);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Audio recording is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
      const mimeType = preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      recordingStreamRef.current = stream;
      recordingChunksRef.current = [];
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordingMimeType = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0].trim() || 'audio/webm';
        const extension = recordingMimeType.includes('ogg') ? 'ogg' : 'webm';
        const recordedBlob = new Blob(recordingChunksRef.current, { type: recordingMimeType });
        const durationSeconds = startedAtRef.current
          ? Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000))
          : 0;

        if (recordedBlob.size > 0) {
          const recordedFile = new File([recordedBlob], `voice-note.${extension}`, { type: recordingMimeType });
          onRecordingComplete?.(recordedFile, durationSeconds);
          setError('');
        }

        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;
        startedAtRef.current = null;
        stopRecordingStream();
        clearTimer();
        setIsRecording(false);
        setElapsedSeconds(0);
      };

      recorder.onerror = () => {
        setError('Recording failed.');
        mediaRecorderRef.current = null;
        recordingChunksRef.current = [];
        startedAtRef.current = null;
        stopRecordingStream();
        clearTimer();
        setIsRecording(false);
        setElapsedSeconds(0);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setError('');

      clearTimer();
      timerRef.current = window.setInterval(() => {
        if (!startedAtRef.current) {
          return;
        }

        const nextElapsed = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));
        setElapsedSeconds(nextElapsed);

        if (maxDurationSeconds && nextElapsed >= maxDurationSeconds) {
          stopRecording();
        }
      }, 250);
    } catch {
      setError('Microphone permission is required to record voice.');
      stopRecordingStream();
      clearTimer();
      setIsRecording(false);
      setElapsedSeconds(0);
    }
  }, [clearTimer, maxDurationSeconds, onRecordingComplete, stopRecording, stopRecordingStream]);

  useEffect(() => () => {
    stopRecording();
    clearTimer();
    stopRecordingStream();
  }, [clearTimer, stopRecording, stopRecordingStream]);

  return {
    isRecording,
    elapsedSeconds,
    error,
    setError,
    startRecording,
    stopRecording,
  };
};
